import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useBranding } from '../Context/BrandingContext';
import { useLoginModal } from '../Context/LoginModalContext';
import { getSessionToken, getUserDetails } from '../Services/services';
import { ROLES, hasRole } from '../utils/roles';
import { getQuotes, getQuoteLines, getQuoteCounts } from '../api/quotes';
import { getCatalogOfferings } from '../api/inventory';
import AppHeader from './AppHeader';
import PermissionGate from './PermissionGate';
import { isInsufficientPermissionError } from '../utils/permissionErrors';
import { useInfiniteList } from '../hooks/useInfiniteList';
import { usePersonalizations } from '../hooks/usePersonalizations';
import SortChipRow from './marketplace/SortChipRow';

const PARTS_STATUS_MAP = {
  'In Progress': { label: 'In Progress', color: '#2563eb', bg: '#eff6ff' },
  'Sent to Customer': { label: 'Quoted', color: '#059669', bg: '#ecfdf5' },
};

const REPAIR_STATUS_MAP = {
  'In Progress': { label: 'In Progress', color: '#2563eb', bg: '#eff6ff' },
  'Sent to Customer': { label: 'Quoted', color: '#059669', bg: '#ecfdf5' },
};

function mapStatus(sfStatus, isRepair) {
  const map = isRepair ? REPAIR_STATUS_MAP : PARTS_STATUS_MAP;
  return map[sfStatus] || { label: sfStatus || 'Unknown', color: '#6b7280', bg: '#f3f4f6' };
}

function formatPrice(val) {
  if (val == null) return null;
  const n = Number(val);
  return isNaN(n) ? null : `$${n.toFixed(2)}`;
}

// A zero quote total means nothing on the quote carries an amount yet, and $0.00 would
// read as free.
function formatQuoteTotal(val) {
  if (val == null || Number(val) === 0) return null;
  return formatPrice(val);
}

// Dispositions the seller sets that change what the buyer does next. Negotiated - Won
// and Negotiated - Lost are internal sales outcomes and stay out of the portal.
const CUSTOMER_DISPOSITIONS = {
  'No Quote': { label: 'No Quote', color: '#b45309', bg: '#fef3c7' },
  'Alternate PN Quote': { label: 'Alternate PN quoted', color: '#1d4ed8', bg: '#eff6ff' },
  'Make Offer': { label: 'Make an offer', color: '#6d28d9', bg: '#f5f3ff' },
};

const CANCELLED_STATE = { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6' };
const PENDING_STATE = { label: 'Awaiting price', color: '#6b7280', bg: '#f3f4f6' };

function hasQuotedAmount(price, total) {
  const p = price != null ? Number(price) : null;
  const t = total != null ? Number(total) : null;
  if (p != null && !Number.isNaN(p) && p !== 0) return true;
  if (t != null && !Number.isNaN(t) && t !== 0) return true;
  return false;
}

// A declined line is stamped Quoted in Salesforce with a zero amount, so neither the
// status nor the amount separates it from a line nobody has priced yet. The seller's
// disposition is the only field that does.
function lineState(line) {
  const disposition = line.Disposition ?? line.inscor__Disposition__c ?? null;
  const price = line.Price ?? line.inscor__Price__c ?? null;
  const total = line.LineTotal ?? line.inscor__Line_Total__c ?? null;

  if (disposition === 'No Quote') return { quoted: false, ...CUSTOMER_DISPOSITIONS['No Quote'] };
  if ((line.Status ?? line.inscor__Status__c) === 'Cancelled') return { quoted: false, ...CANCELLED_STATE };
  if (hasQuotedAmount(price, total)) return { quoted: true, price, total };

  return { quoted: false, ...(CUSTOMER_DISPOSITIONS[disposition] || PENDING_STATE) };
}

const SEARCH_DEBOUNCE_MS = 400;
const LINE_FETCH_CONCURRENCY = 3;

const QUOTE_SORT_CHIPS = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'name_asc', label: 'Quote number (A–Z)' },
  { value: 'name_desc', label: 'Quote number (Z–A)' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'New', label: 'New' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Sent to Customer', label: 'Quoted' },
  { value: 'Closed - Won', label: 'Closed - Won' },
  { value: 'Closed - Lost', label: 'Closed - Lost' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const URGENCY_OPTIONS = [
  { value: '', label: 'All urgencies' },
  { value: 'Routine', label: 'Routine' },
  { value: 'High', label: 'High' },
  { value: 'Low', label: 'Low' },
];

function QuotesPlaceholder() {
  return (
    <div style={{
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      padding: '20px 24px',
    }}>
      <div style={{ height: 12, width: '40%', backgroundColor: '#e5e7eb', borderRadius: 4, marginBottom: 16 }} />
      <div style={{ height: 72, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 12 }} />
      <div style={{ height: 72, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }} />
    </div>
  );
}

export default function MyRFQs() {
  const { branding } = useBranding();
  const { openLogin } = useLoginModal();
  const auth = useSelector((state) => state.auth);

  const [tabCounts, setTabCounts] = useState({ parts: 0, repair: 0 });
  const [userRole, setUserRole] = useState(auth.role || ROLES.PENDING);
  const [roleReady, setRoleReady] = useState(!!auth.role);
  const [activeTab, setActiveTab] = useState('parts');

  // Marketplace listing toggles (Salesforce feature management) gate which quote
  // tabs are shown, mirroring the search catalog: inventory listing off hides the
  // Parts Quotes tab, repair capability listing off hides the Repair Quotes tab.
  // Fail open (both visible) so a failed lookup never hides a customer's quotes.
  const [offerings, setOfferings] = useState({ parts_catalog_offered: true, repair_capabilities_offered: true });
  const [offeringsLoaded, setOfferingsLoaded] = useState(false);
  const showParts = offerings.parts_catalog_offered;
  const showRepair = offerings.repair_capabilities_offered;
  const activeTabOffered = (activeTab === 'parts' && showParts) || (activeTab === 'repair' && showRepair);
  const noListingsEnabled = offeringsLoaded && !showParts && !showRepair;

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [statusFilter, setStatusFilter] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState('');
  const [portalOnly, setPortalOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount = [statusFilter, urgencyFilter, portalOnly ? 'portal' : ''].filter(Boolean).length;

  const [expandedIds, setExpandedIds] = useState(new Set());
  const [lines, setLines] = useState({});
  const [linesLoading, setLinesLoading] = useState({});
  const { prefs, setPref } = usePersonalizations();
  const linesPrefKey = activeTab === 'repair' ? 'quotes.repair.linesExpanded' : 'quotes.parts.linesExpanded';
  const linesExpanded = !!prefs[linesPrefKey];

  const isGuest = !getSessionToken();
  const isPending = roleReady && !isGuest && !hasRole(userRole, ROLES.STANDARD);

  useEffect(() => {
    if (isGuest) { setRoleReady(true); return; }
    getUserDetails()
      .then((resp) => setUserRole(resp?.data?.role || auth.role || ROLES.PENDING))
      .catch(() => setUserRole(auth.role || ROLES.PENDING))
      .finally(() => setRoleReady(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isGuest) return undefined;
    let cancelled = false;
    getCatalogOfferings()
      .then((data) => {
        if (cancelled || !data) return;
        setOfferings({
          parts_catalog_offered: data.parts_catalog_offered !== false,
          repair_capabilities_offered: data.repair_capabilities_offered !== false,
        });
      })
      .catch(() => { /* fail open — keep both tabs visible */ })
      .finally(() => { if (!cancelled) setOfferingsLoaded(true); });
    return () => { cancelled = true; };
  }, [isGuest]);

  // If the current tab is disabled by the listing toggles, fall back to the other.
  useEffect(() => {
    if (!offeringsLoaded) return;
    if (activeTab === 'parts' && !showParts && showRepair) setActiveTab('repair');
    else if (activeTab === 'repair' && !showRepair && showParts) setActiveTab('parts');
  }, [offeringsLoaded, showParts, showRepair, activeTab]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (isGuest) return;
    getQuoteCounts()
      .then((data) => {
        const kindCounts = data?.kindCounts;
        if (kindCounts) setTabCounts({ parts: kindCounts.parts || 0, repair: kindCounts.repair || 0 });
      })
      .catch(() => {});
  }, [isGuest]);

  const fetchPage = useCallback((page, limit) => getQuotes({
    page,
    limit,
    sort: sortBy,
    keyword: searchTerm || undefined,
    status: statusFilter || undefined,
    urgency: urgencyFilter || undefined,
    portalOnly: portalOnly || undefined,
    kind: activeTab,
  }), [sortBy, searchTerm, statusFilter, urgencyFilter, portalOnly, activeTab]);

  const resetKey = JSON.stringify({ sortBy, searchTerm, statusFilter, urgencyFilter, portalOnly, activeTab });
  const listEnabled = !isGuest && roleReady && !isPending && offeringsLoaded && activeTabOffered;
  const {
    records: quotes,
    totalCount,
    loading,
    loadingMore,
    error,
    hasMore,
    sentinelRef,
    reload,
  } = useInfiniteList({
    cacheKey: `quotes:${activeTab}`,
    fetchPage,
    resetKey,
    enabled: listEnabled,
  });

  const linesRef = useRef(lines);
  linesRef.current = lines;
  const linesLoadingRef = useRef(linesLoading);
  linesLoadingRef.current = linesLoading;

  const loadLines = useCallback(async (quoteId) => {
    if (linesRef.current[quoteId] || linesLoadingRef.current[quoteId]) return;
    setLinesLoading((prev) => ({ ...prev, [quoteId]: true }));
    try {
      const data = await getQuoteLines(quoteId);
      const list = Array.isArray(data) ? data : (data?.lines || data?.records || []);
      setLines((prev) => ({ ...prev, [quoteId]: list }));
    } catch {
      setLines((prev) => ({ ...prev, [quoteId]: [] }));
    } finally {
      setLinesLoading((prev) => ({ ...prev, [quoteId]: false }));
    }
  }, []);

  const applyExpandAll = useCallback((rows) => {
    const ids = rows.map((q) => q.Id).filter(Boolean);
    setExpandedIds((prev) => {
      const same = ids.length === prev.size && ids.every((id) => prev.has(id));
      return same ? prev : new Set(ids);
    });
  }, []);

  useEffect(() => {
    if (!linesExpanded || quotes.length === 0) return undefined;
    applyExpandAll(quotes);
    const pending = quotes.map((q) => q.Id).filter((id) => id && !linesRef.current[id]);
    if (pending.length === 0) return undefined;
    let cancelled = false;
    const run = async () => {
      for (let i = 0; i < pending.length && !cancelled; i += LINE_FETCH_CONCURRENCY) {
        await Promise.all(pending.slice(i, i + LINE_FETCH_CONCURRENCY).map((id) => loadLines(id)));
      }
    };
    run();
    return () => { cancelled = true; };
  }, [linesExpanded, quotes, loadLines, applyExpandAll]);

  const handleExpand = async (quoteId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(quoteId)) next.delete(quoteId);
      else next.add(quoteId);
      return next;
    });
    loadLines(quoteId);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setExpandedIds(new Set());
  };

  if (isGuest) return null;

  if (isPending) {
    return (
      <div style={styles.page}>
        <AppHeader title="Quotes" />
        <main style={styles.main}>
          <div style={styles.container}>
            <PermissionGate blocked primaryColor={branding.primaryColor} minHeight={380}>
              <QuotesPlaceholder />
            </PermissionGate>
          </div>
        </main>
      </div>
    );
  }

  const permissionBlocked = isInsufficientPermissionError(error);
  const hasActiveFilter = !!searchTerm || portalOnly || !!statusFilter || !!urgencyFilter;
  const isTrulyEmpty = !loading && totalCount === 0 && !hasActiveFilter;
  const hasNoMatches = !loading && totalCount === 0 && hasActiveFilter;

  return (
    <div style={styles.page}>
      <AppHeader title="Quotes" />

      <main style={styles.main}>
        <div style={styles.container}>

          {/* Tabs — each is hidden when its marketplace listing toggle is off */}
          <div style={styles.tabRow}>
            {showParts && (
              <button
                type="button"
                style={{
                  ...styles.tab,
                  ...(activeTab === 'parts'
                    ? { borderBottomColor: branding.primaryColor, color: branding.primaryColor }
                    : {}),
                }}
                onClick={() => handleTabChange('parts')}
              >
                Parts Quotes
                {tabCounts.parts > 0 && (
                  <span style={{
                    ...styles.tabBadge,
                    backgroundColor: activeTab === 'parts' ? branding.primaryColor : '#9ca3af',
                  }}>
                    {tabCounts.parts}
                  </span>
                )}
              </button>
            )}
            {showRepair && (
              <button
                type="button"
                style={{
                  ...styles.tab,
                  ...(activeTab === 'repair'
                    ? { borderBottomColor: branding.primaryColor, color: branding.primaryColor }
                    : {}),
                }}
                onClick={() => handleTabChange('repair')}
              >
                Repair Quotes
                {tabCounts.repair > 0 && (
                  <span style={{
                    ...styles.tabBadge,
                    backgroundColor: activeTab === 'repair' ? branding.primaryColor : '#9ca3af',
                  }}>
                    {tabCounts.repair}
                  </span>
                )}
              </button>
            )}
            <div style={styles.tabSpacer} />
            <button
              type="button"
              style={styles.refreshBtn}
              onClick={() => {
                const next = !linesExpanded;
                setPref(linesPrefKey, next);
                if (next) applyExpandAll(quotes);
                else setExpandedIds(new Set());
              }}
            >
              {linesExpanded ? 'Collapse all' : 'Expand all'}
            </button>
            <button
              type="button"
              style={styles.refreshBtn}
              onClick={reload}
              disabled={loading}
              title="Refresh from Salesforce"
            >
              {loading ? '…' : '↻ Refresh'}
            </button>
          </div>

          {/* Search — server-side, matches Quote Number or part number */}
          <div style={styles.searchRow}>
            <div style={styles.searchWrap}>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by Quote Number or Part Number…"
                style={styles.searchInput}
              />
              {loading && searchInput && (
                <span style={styles.searchSpinner} aria-label="Searching">
                  <span className="spinner-inline" />
                </span>
              )}
            </div>
            <button
              type="button"
              style={filtersOpen ? styles.filtersToggleBtnActive : styles.filtersToggleBtn}
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
            >
              Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              <span style={{
                ...styles.expandChevron,
                transform: filtersOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}>
                <svg viewBox="0 0 24 24" fill="none" width={14} height={14}
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m6 9 6 6 6-6"/>
                </svg>
              </span>
            </button>
          </div>

          <SortChipRow
            options={QUOTE_SORT_CHIPS}
            value={sortBy}
            onChange={setSortBy}
            primaryColor={branding.primaryColor}
          />

          {/* Filters — applied server-side */}
          {filtersOpen && (
            <div style={styles.filterPanel}>
              <div style={styles.filterField}>
                <label style={styles.filterLabel}>Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); }}
                  style={styles.sortSelect}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div style={styles.filterField}>
                <label style={styles.filterLabel}>Urgency</label>
                <select
                  value={urgencyFilter}
                  onChange={(e) => { setUrgencyFilter(e.target.value); }}
                  style={styles.sortSelect}
                >
                  {URGENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <label style={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={portalOnly}
                  onChange={(e) => { setPortalOnly(e.target.checked); }}
                />
                Portal quotes only{portalOnly ? ` (${totalCount})` : ''}
              </label>
            </div>
          )}

          {loading && <p>Loading Quotes…</p>}

          <PermissionGate blocked={permissionBlocked} primaryColor={branding.primaryColor} minHeight={320}>
            {noListingsEnabled && (
              <div style={styles.emptyCard}>
                <p style={styles.muted}>
                  Quotes are unavailable because the marketplace inventory and repair capability listings are turned off.
                </p>
              </div>
            )}

            {isTrulyEmpty && !noListingsEnabled && (
              <div style={styles.emptyCard}>
                <p style={styles.muted}>
                  {activeTab === 'repair'
                    ? 'You have no repair quotes yet. Add repair capabilities from the catalog to your cart to create one.'
                    : (
                      <>You have no parts quotes yet. Submit items from your <Link to="/cart">cart</Link> to create one.</>
                    )}
                </p>
              </div>
            )}

            {hasNoMatches && (
              <div style={styles.emptyCard}>
                <p style={styles.muted}>No quotes match your search or filter.</p>
              </div>
            )}

            {!loading && quotes.map((q) => {
              const isRepair = activeTab === 'repair';
              const sfStatus = q.Status || q.inscor__Status__c;
              const status = mapStatus(sfStatus, isRepair);
              const isExpanded = expandedIds.has(q.Id);
              const isReady = sfStatus === 'Sent to Customer';
              const quoteLines = lines[q.Id] || [];
              const linesBusy = linesLoading[q.Id];

              const customerName = q.inscor__Customer__c?.Name || '—';
              const urgency = q.inscor__Urgency__c || '—';
              const numberOfLines = q.inscor__Number_of_Lines__c ?? q.LineCount ?? q.inscor__Line_Count__c ?? '—';
              const createDate = q.inscor__Create_Date__c
                ? new Date(q.inscor__Create_Date__c).toLocaleDateString()
                : (q.CreatedDate ? new Date(q.CreatedDate).toLocaleDateString() : '—');
              const quoteTotal = formatQuoteTotal(q.inscor__Quote_Total__c) || 'Not quoted';
              const quotedLineCount = quoteLines.filter((l) => lineState(l).quoted).length;
              const quotedRollup = isReady && quoteLines.length > 0
                ? `${quotedLineCount} of ${quoteLines.length} lines quoted`
                : null;

              return (
                <div key={q.Id} style={styles.rfqCard}>
                  <div
                    style={{ ...styles.rfqHeader, cursor: 'pointer' }}
                    onClick={() => handleExpand(q.Id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleExpand(q.Id); }
                    }}
                    aria-expanded={isExpanded}
                  >
                    <div style={styles.rfqLeft}>
                      <span style={styles.rfqName}>{q.Name}</span>
                      <span style={{ ...styles.statusBadge, color: status.color, backgroundColor: status.bg }}>
                        {status.label}
                      </span>
                    </div>
                    <div style={styles.rfqRight}>
                      <span style={styles.linesHint}>{isExpanded ? 'Hide lines' : 'View lines'}</span>
                      <span style={{
                        ...styles.expandChevron,
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}>
                        <svg viewBox="0 0 24 24" fill="none" width={18} height={18}
                          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m6 9 6 6 6-6"/>
                        </svg>
                      </span>
                    </div>
                  </div>

                  <div style={styles.rfqDetailRow}>
                    <div style={styles.rfqDetailItem}>
                      <span style={styles.rfqDetailLabel}>Customer</span>
                      <span style={styles.rfqDetailValue}>{customerName}</span>
                    </div>
                    <div style={styles.rfqDetailItem}>
                      <span style={styles.rfqDetailLabel}>Urgency</span>
                      <span style={styles.rfqDetailValue}>{urgency}</span>
                    </div>
                    <div style={styles.rfqDetailItem}>
                      <span style={styles.rfqDetailLabel}>Number of Lines</span>
                      <span style={styles.rfqDetailValue}>{numberOfLines}</span>
                    </div>
                    <div style={styles.rfqDetailItem}>
                      <span style={styles.rfqDetailLabel}>Create Date</span>
                      <span style={styles.rfqDetailValue}>{createDate}</span>
                    </div>
                    <div style={styles.rfqDetailItem}>
                      <span style={styles.rfqDetailLabel}>Quote Total</span>
                      <span style={styles.rfqDetailValue}>{quoteTotal}</span>
                      {quotedRollup && <span style={styles.rfqDetailHint}>{quotedRollup}</span>}
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={styles.rfqBody}>
                      {linesBusy && <p style={styles.muted}>Loading lines…</p>}
                      {!linesBusy && quoteLines.length === 0 && (
                        <p style={styles.muted}>No lines found.</p>
                      )}
                      {!linesBusy && quoteLines.length > 0 && (
                        <>
                          <div style={styles.lineTableWrap}>
                            <table style={styles.lineTable}>
                              <thead>
                                <tr>
                                  <th style={styles.lineTh}>Part</th>
                                  <th style={styles.lineTh}>Condition</th>
                                  {isRepair && <th style={styles.lineTh}>Service</th>}
                                  <th style={styles.lineTh}>Qty</th>
                                  <th style={styles.lineTh}>Price</th>
                                  <th style={styles.lineTh}>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {quoteLines.map((l, idx) => {
                                  const state = lineState(l);
                                  const service = l.QuoteType || l.inscor__Quote_Type__c;

                                  return (
                                    <tr key={l.Id} style={idx % 2 === 0 ? styles.lineRowEven : undefined}>
                                      <td style={styles.lineTd}>
                                        {l.ProductName || l.inscor__Product__c?.Name || '—'}
                                      </td>
                                      <td style={styles.lineTd}>
                                        {l.Condition || l.inscor__Condition_Code__c?.Name || '—'}
                                      </td>
                                      {isRepair && (
                                        <td style={styles.lineTd}>
                                          {service === 'Work Order' ? 'Repair / MRO' : (service || '—')}
                                        </td>
                                      )}
                                      <td style={styles.lineTd}>
                                        {l.Quantity ?? l.inscor__Quantity__c ?? '—'}
                                      </td>
                                      {state.quoted ? (
                                        <>
                                          <td style={styles.lineTd}>
                                            {formatPrice(state.price) || '—'}
                                          </td>
                                          <td style={styles.lineTd}>
                                            {formatPrice(state.total) || '—'}
                                          </td>
                                        </>
                                      ) : (
                                        <td style={styles.lineTd} colSpan={2}>
                                          <span style={{
                                            ...styles.lineStateBadge,
                                            color: state.color,
                                            backgroundColor: state.bg,
                                          }}>
                                            {state.label}
                                          </span>
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          {/* ACON-61: Quotes are view-only — purchase via Cart → Quoted Items → Move to Cart */}
                          {isReady && !isRepair && (
                            <div style={styles.purchaseBar}>
                              <span style={{ fontSize: 13, color: '#6b7280' }}>
                                Ready to buy? Open <Link to="/cart">Cart</Link> → Quoted Items → Move to Cart, then checkout.
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </PermissionGate>

          <div ref={sentinelRef} style={{ height: 1 }} />
          {loadingMore && <p style={styles.muted}>Loading more quotes…</p>}
          {!loading && !hasMore && totalCount > 0 && (
            <p style={{ ...styles.muted, textAlign: 'center', marginTop: 16 }}>No more quotes</p>
          )}

          {error && !permissionBlocked && (
            <p style={styles.err}>{error.message || 'Failed to load quotes'}</p>
          )}
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', backgroundColor: 'var(--background)', color: 'var(--foreground)' },
  refreshBtn: {
    border: '1px solid #d1d5db',
    background: 'transparent',
    color: '#374151',
    borderRadius: 8,
    padding: '6px 12px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
  main: { padding: '24px 24px' },
  container: { maxWidth: 1100, margin: '0 auto' },
  err: { color: 'var(--destructive)', fontSize: 14 },
  muted: { color: 'var(--muted-foreground)', fontSize: 14 },
  emptyCard: {
    backgroundColor: 'var(--card)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 24,
  },

  tabRow: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: '2px solid var(--border)',
    marginBottom: 20,
    gap: 0,
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    marginBottom: -2,
    padding: '10px 18px',
    fontWeight: 600,
    fontSize: 14,
    color: 'var(--muted-foreground)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'color 0.15s',
  },
  tabBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 99,
    padding: '1px 7px',
    fontSize: 11,
    fontWeight: 700,
    color: 'white',
    minWidth: 20,
  },
  tabSpacer: { flex: 1 },

  searchRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  filtersToggleBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    border: '1px solid var(--border)',
    background: 'var(--card)',
    color: 'var(--foreground)',
    borderRadius: 8,
    padding: '8px 14px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  filtersToggleBtnActive: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    border: '1px solid var(--primary)',
    background: 'var(--secondary)',
    color: 'var(--foreground)',
    borderRadius: 8,
    padding: '8px 14px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  filterPanel: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: 16,
    backgroundColor: 'var(--secondary)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '14px 16px',
    marginBottom: 16,
  },
  filterField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 160,
  },
  filterLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--muted-foreground)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  searchWrap: {
    position: 'relative',
    flex: '1 1 240px',
    minWidth: 200,
    display: 'flex',
    alignItems: 'center',
  },
  searchSpinner: {
    position: 'absolute',
    right: 14,
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 36px 8px 12px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    fontSize: 13,
    backgroundColor: 'var(--card)',
    color: 'var(--foreground)',
  },
  sortSelect: {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    fontSize: 13,
    backgroundColor: 'var(--card)',
    color: 'var(--foreground)',
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--foreground)',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  pager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    marginTop: 20,
  },
  pagerBtn: {
    border: '1px solid #d1d5db',
    background: 'transparent',
    color: '#374151',
    borderRadius: 8,
    padding: '6px 14px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
  pagerBtnDisabled: {
    border: '1px solid #e5e7eb',
    background: '#f3f4f6',
    color: '#9ca3af',
    borderRadius: 8,
    padding: '6px 14px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'not-allowed',
  },
  pagerInfo: { fontSize: 13, color: 'var(--muted-foreground)', fontWeight: 500 },

  rfqCard: {
    backgroundColor: 'var(--card)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    marginBottom: 12,
    overflow: 'hidden',
  },
  rfqHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 20px',
    cursor: 'pointer',
    gap: 12,
    flexWrap: 'wrap',
    borderRadius: '12px 12px 0 0',
    transition: 'background-color 0.12s',
    userSelect: 'none',
  },
  rfqLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  rfqName: { fontWeight: 700, fontSize: 15 },
  statusBadge: {
    display: 'inline-block',
    borderRadius: 6,
    padding: '3px 10px',
    fontSize: 12,
    fontWeight: 600,
  },
  rfqRight: { display: 'flex', alignItems: 'center', gap: 16 },
  rfqDetailRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 24,
    padding: '0 20px 14px',
    borderBottom: 'none',
  },
  rfqDetailItem: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 100 },
  rfqDetailLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--muted-foreground)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  rfqDetailValue: { fontSize: 14, fontWeight: 500, color: 'var(--foreground)' },
  rfqDetailHint: { fontSize: 11, fontWeight: 500, color: 'var(--muted-foreground)' },
  linesHint: { fontSize: 12, fontWeight: 600, color: 'var(--muted-foreground)' },
  expandChevron: {
    display: 'flex',
    alignItems: 'center',
    color: 'var(--muted-foreground)',
    transition: 'transform 0.18s ease',
  },

  rfqBody: {
    borderTop: '1px solid var(--border)',
    padding: '16px 20px',
  },
  lineTableWrap: { overflowX: 'auto' },
  lineTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  lineTh: {
    textAlign: 'left',
    padding: '8px 10px',
    backgroundColor: 'var(--muted)',
    fontWeight: 600,
    borderBottom: '2px solid var(--border)',
    whiteSpace: 'nowrap',
  },
  lineTd: { padding: '8px 10px', borderBottom: '1px solid var(--border)' },
  lineRowEven: { backgroundColor: 'var(--secondary)' },
  lineStateBadge: {
    display: 'inline-block',
    borderRadius: 6,
    padding: '2px 8px',
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  lineRowOrdered: { opacity: 0.55 },
  orderedBadge: {
    display: 'inline-block',
    borderRadius: 6,
    padding: '2px 8px',
    fontSize: 11,
    fontWeight: 700,
    color: '#166534',
    backgroundColor: '#dcfce7',
    whiteSpace: 'nowrap',
  },
  allOrderedNote: { fontSize: 13, color: 'var(--muted-foreground)', fontStyle: 'italic' },

  purchaseBar: { marginTop: 16, display: 'flex', justifyContent: 'flex-end' },
  purchaseBtn: {
    color: 'white',
    border: 'none',
    borderRadius: 8,
    padding: '10px 22px',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 14,
  },
};
